import React, { useCallback, useMemo, useState } from 'react';
import {
  defaultPolicyPremiumDetailsData,
  IUpdatePolicyPremiumDetailsRequest,
} from 'types/order/title/IPolicyPremiumDetailsResponse';
import {
  useGetPolicyPremiumDetailsQuery,
  useUpdatePolicyPremiumDetailsMutation,
} from '../../../../../../API/order/title/PolicyPremiumDetailsAPI';
import { checkForMutationSuccess } from '../../../../../../API/checkForMutationSuccess';
import { useParams } from 'react-router-dom';
import { UUID } from '../../../../../../types/shared/UUID';
import { ErrorMessageTW } from '../../../../../../tailwindComponents/errorMessage/ErrorMessageTW';
import { LoadingSpinnerTW } from '../../../../../../tailwindComponents/loadingSpinnerTW/LoadingSpinnerTW';
import {
  LenderSellerPercentageFormValidation,
  OwnerSellerPercentageFormValidation,
} from '../../../../../../validation/order/title/PolicyPremiumDetailsFormsValidation';
import { Form, Formik, useFormikContext } from 'formik';
import { Button, Grid, NumberField } from '../../../../../../ui-toolkit';

export const SettlementPercentageDisplayAndForm: React.FC<{ isOwner: boolean }> = ({ isOwner }) => {
  const { orderId } = useParams<{ orderId: UUID }>();
  const [showForm, setShowForm] = useState(false);

  const {
    data: { manuallyEdited, ownerSellerPaidPercent, lenderSellerPaidPercent } = defaultPolicyPremiumDetailsData,
  } = useGetPolicyPremiumDetailsQuery(orderId);

  const [updatePolicyPremiumDetailsMutation, { isLoading: isUpdateLoading, error: updateError }] =
    useUpdatePolicyPremiumDetailsMutation();

  const onUpdateTitlePolicyPremiumDetails = useCallback(
    async (updateRequest: IUpdatePolicyPremiumDetailsRequest) => {
      const result = await updatePolicyPremiumDetailsMutation({ orderId, updateRequest });
      if (checkForMutationSuccess(result, 'changing Payment Percentages successfully')) {
        setShowForm(false);
      }
    },
    [updatePolicyPremiumDetailsMutation, orderId]
  );

  const formProps = useMemo(() => {
    if (isOwner) {
      return {
        name: 'ownerSellerPaidPercent',
        initialValues: { ownerSellerPaidPercent },
        validationSchema: OwnerSellerPercentageFormValidation,
      };
    }
    return {
      name: 'lenderSellerPaidPercent',
      initialValues: { lenderSellerPaidPercent },
      validationSchema: LenderSellerPercentageFormValidation,
    };
  }, [isOwner, ownerSellerPaidPercent, lenderSellerPaidPercent]);

  const onResetPaidPercentage = useCallback(() => {
    setShowForm(false);
  }, [setShowForm]);

  return (
    <>
      <ErrorMessageTW rtkError={updateError} />
      <LoadingSpinnerTW isLoading={isUpdateLoading}>
        <Formik
          initialValues={formProps.initialValues}
          onSubmit={onUpdateTitlePolicyPremiumDetails}
          onReset={onResetPaidPercentage}
          validateOnMount
          validationSchema={formProps.validationSchema}
          enableReinitialize
        >
          <Form>
            <Grid columnCount={2}>
              <NumberField
                name={formProps.name}
                suffix={'%'}
                disabled={manuallyEdited || !showForm}
                readOnly={!showForm}
              />
              <FormButtons setShowForm={setShowForm} showForm={showForm} manuallyEdited={manuallyEdited} />
            </Grid>
          </Form>
        </Formik>
      </LoadingSpinnerTW>
    </>
  );
};

const FormButtons: React.FC<{
  setShowForm: React.Dispatch<React.SetStateAction<boolean>>;
  showForm: boolean;
  manuallyEdited: boolean;
}> = ({ setShowForm, showForm, manuallyEdited }) => {
  const { isValid, dirty, isSubmitting } = useFormikContext();

  if (!showForm) {
    return (
      <Button intent={'secondary'} onClick={() => setShowForm(true)} disabled={manuallyEdited}>
        Edit
      </Button>
    );
  }
  return (
    <Grid columnCount={2}>
      <Button intent={'primary'} type={'submit'} disabled={!isValid || !dirty || isSubmitting}>
        Update
      </Button>
      <Button intent={'secondary'} type={'reset'}>
        Cancel
      </Button>
    </Grid>
  );
};
